import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import {
    type FeedingDashboardOverview,
    type FeedingDataSources,
    type FeedingRefreshStatus,
    penDisplayNames,
} from '@/domain/feedingDashboard';
import { formatDateTimeWorded } from '@/utils/date';

import { BioscopeCoverageNotice } from './components/BioscopeCoverageNotice';
import { DataSourcesLink } from './components/DataSourcesLink';
import { FeedingServiceNotice } from './components/FeedingServiceNotice';
import { systemLabels } from './components/systemLabel';

import styles from './FeedingDashboard.module.scss';

interface FeedingRefreshHintProps {
    /** Absent before any response has arrived, which shows no hint. */
    feeding: FeedingRefreshStatus | undefined;
    /**
     * True once the live-refresh poll budget's fast and slow tiers are spent while
     * `feeding.refreshing` stays true. Polling keeps going at a slower, trailing
     * interval past this point, so this only changes which sentence is shown.
     */
    pollingSlow: boolean;
}

export function FeedingRefreshHint(props: FeedingRefreshHintProps) {
    const { feeding, pollingSlow } = props;
    const { t } = useTranslation();

    if (!feeding?.refreshing) return null;
    return (
        <p className={styles.refreshingHint}>
            {pollingSlow
                ? feeding.lastFetchedAt
                    ? t('feedingModule.refresh.hintExhausted', {
                          time: formatDateTimeWorded(feeding.lastFetchedAt),
                      })
                    : t('feedingModule.refresh.hintExhaustedNoTime')
                : t('feedingModule.refresh.hint')}
        </p>
    );
}

/**
 * Whether the period has any pen, which is what every part of the page needs. Without
 * one the empty-period notice stands alone.
 */
export function hasPeriodPens(overview: FeedingDashboardOverview): boolean {
    return overview.availablePens.length > 0;
}

/**
 * Whether the KPIs, the pen table and the bar charts have figures to show: pens in the
 * period and at least one complete day. Otherwise an empty state explains why they are
 * left out, instead of a row of dashes that would read as missing data; the timelines
 * still draw today.
 */
export function hasEvaluatedFigures(overview: FeedingDashboardOverview): boolean {
    return hasPeriodPens(overview) && overview.closingDay !== null;
}

/**
 * The busy line while the next response loads. Mounted whether or not it has text, so a
 * screen reader announces the text when it arrives: a live region that appears together
 * with its text is often not read at all. Out of the page's flow while empty, so it adds
 * no gap of its own.
 */
function UpdatingStatus(props: { updating: boolean }) {
    const { t } = useTranslation();
    return (
        <p className={props.updating ? styles.updatingStatus : styles.idleStatus} role="status">
            {props.updating ? t('feedingModule.period.updating') : null}
        </p>
    );
}

/**
 * The sources a provider mapping fills. Bioscope weight and pellet also report
 * notConfigured, for a pen without a camera or without 3.0 hardware, which no setting
 * on the data sources page changes.
 */
const MAPPED_SOURCES: readonly (keyof FeedingDataSources)[] = [
    'feedActual',
    'feedPlan',
    'weightPmsPlan',
    'weightPmsActual',
    'fcrPms',
    'fishCount',
];

export function hasUnmappedSignal(overview: FeedingDashboardOverview): boolean {
    return overview.pens.some((pen) =>
        MAPPED_SOURCES.some((source) => pen.dataSources[source] === 'notConfigured')
    );
}

interface FeedingDashboardNoticesProps {
    overview: FeedingDashboardOverview;
    /** True while `overview` is the previous selection's, kept until the next response. */
    updating?: boolean;
    /** The site's data sources page; null leaves the unmapped-signal notice out. */
    dataSourcesHref?: string | null;
}

export function FeedingDashboardNotices(props: FeedingDashboardNoticesProps) {
    const { overview, updating = false, dataSourcesHref = null } = props;
    const { t } = useTranslation();
    const system = systemLabels(t, overview.config.pmsSystemName).name;
    const names = useMemo(() => penDisplayNames(overview.availablePens), [overview.availablePens]);
    // The notices below describe the previous response too, so they dim with its figures;
    // the updating line itself stays at full contrast.
    const stale = updating ? styles.stale : undefined;

    if (!hasPeriodPens(overview)) {
        return (
            <>
                <UpdatingStatus updating={updating} />
                <p className={classNames(styles.emptySelection, stale)}>
                    {t('feedingModule.emptyPeriod', { system })}
                </p>
            </>
        );
    }

    const figures = hasEvaluatedFigures(overview);
    return (
        <>
            <UpdatingStatus updating={updating} />
            {/* First, since it says why most of the page is missing. Only the timelines
                draw a period without a complete day, and they mark today on their own. */}
            {figures ? null : (
                <p className={classNames(styles.emptySelection, stale)}>
                    {t('feedingModule.period.none')}
                </p>
            )}

            {/* Also for a period without a complete day: the timelines then show
                today, and they lose the same values. */}
            <FeedingServiceNotice
                pens={overview.pens}
                names={names}
                system={system}
                className={stale}
            />

            {/* Only with somewhere to send the reader: without the page it names a gap
                the reader cannot close. */}
            {dataSourcesHref !== null && hasUnmappedSignal(overview) ? (
                <p className={classNames(styles.coverageNotice, stale)}>
                    {t('providerConfig.entry.unmappedNotice')}
                    <DataSourcesLink href={dataSourcesHref} labelKey="providerConfig.entry.setUp" />
                </p>
            ) : null}

            {/* Each reason a pen is left out asks something different of the operator:
                wait, check why no estimates exist, or nothing at all for a pen that
                has no Bioscope. Without a complete day there are no figures for it to
                explain. It names no pellet figure: those follow pellet data, not the
                Bioscope readiness this counts. */}
            {figures ? (
                <BioscopeCoverageNotice
                    penQuality={overview.summary.penQuality}
                    pens={overview.pens}
                    names={names}
                    className={stale}
                />
            ) : null}
        </>
    );
}
